import { useState, FormEvent } from 'react';
import { useLang } from '@/hooks/useLang';

const copy = {
  EN: {
    tag: 'Contact',
    title: 'Let\'s talk.',
    sub: 'Tell us about your project and we\'ll get back to you within 24 hours.',
    fields: {
      name: 'Full Name',
      email: 'Email Address',
      service: 'Service',
      message: 'Tell us about your project',
      submit: 'Send Message',
    },
    services: ['AI Film', 'AI Series', 'Training Content', 'Games & Applications', 'Other'],
    success: 'Message sent! We\'ll be in touch soon.',
    error: 'Something went wrong. Please try again.',
    charLimit: 'characters remaining',
  },
  TR: {
    tag: 'İletişim',
    title: 'Konuşalım.',
    sub: 'Projenizi anlatın, 24 saat içinde size geri döneceğiz.',
    fields: {
      name: 'Ad Soyad',
      email: 'E-posta Adresi',
      service: 'Hizmet',
      message: 'Projenizi anlatın',
      submit: 'Mesaj Gönder',
    },
    services: ['Yapay Zeka Filmi', 'Yapay Zeka Dizisi', 'Eğitim İçeriği', 'Oyunlar ve Uygulamalar', 'Diğer'],
    success: 'Mesajınız iletildi! Kısa sürede sizinle iletişime geçeceğiz.',
    error: 'Bir hata oluştu. Lütfen tekrar deneyin.',
    charLimit: 'karakter kaldı',
  },
};

export default function ContactSection() {
  const lang = useLang();
  const t = copy[lang];
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const MAX_CHARS = 500;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (message.length > MAX_CHARS) return;
    setStatus('loading');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      if (typeof value === 'string') data.append(key, value);
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/d7bqjrmlfkej60p1k620', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setMessage('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-32 bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-8 md:px-16">
        <div className="flex flex-col lg:flex-row gap-20 items-start">
          {/* Left */}
          <div className="flex-1">
            <span className="text-xs font-semibold text-gray-400 tracking-widest uppercase mb-3 block">{t.tag}</span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight mb-4" style={{ fontFamily: '\'Space Grotesk\', sans-serif' }}>
              {t.title}
            </h2>
            <p className="text-gray-400 text-base leading-relaxed max-w-sm mb-12">{t.sub}</p>

            {/* Info */}
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-gray-400 text-base w-4 h-4 flex items-center justify-center"></i>
                </div>
                <a href="mailto:hello@kodonext.com" className="text-sm text-gray-600 font-medium hover:text-gray-900 transition-colors cursor-pointer">
                  hello@kodonext.com
                </a>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-gray-400 text-base w-4 h-4 flex items-center justify-center"></i>
                </div>
                <span className="text-sm text-gray-600 font-medium">Istanbul, Turkey</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-gray-400 text-base w-4 h-4 flex items-center justify-center"></i>
                </div>
                <span className="text-sm text-gray-600 font-medium">24h Response Time</span>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="flex-1 w-full">
            <div className="bg-white rounded-2xl border border-gray-100 p-8 md:p-10">
              {status === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-gray-900 w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-700 font-semibold">{t.success}</p>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs font-semibold text-gray-400 tracking-wide uppercase mb-2 block">{t.fields.name}</label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="John Doe"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-gray-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-400 tracking-wide uppercase mb-2 block">{t.fields.email}</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="john@company.com"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-gray-300 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-gray-400 tracking-wide uppercase mb-2 block">{t.fields.service}</label>
                    <select
                      name="service"
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-gray-300 transition-colors cursor-pointer appearance-none"
                    >
                      <option value="">—</option>
                      {t.services.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-gray-400 tracking-wide uppercase mb-2 block">{t.fields.message}</label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={MAX_CHARS}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="..."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-gray-300 transition-colors resize-none"
                    />
                    <p className="text-xs text-gray-300 text-right mt-1">{MAX_CHARS - message.length} {t.charLimit}</p>
                  </div>

                  {status === 'error' && (
                    <p className="text-sm text-red-400">{t.error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading' || message.length > MAX_CHARS}
                    className="w-full py-4 bg-gray-900 text-white text-sm font-bold tracking-wide rounded-xl hover:bg-gray-700 transition-all duration-300 cursor-pointer disabled:opacity-50 whitespace-nowrap"
                  >
                    {status === 'loading' ? '...' : t.fields.submit}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
